import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";

const schema = z.object({
  reporter_name: z.string().trim().min(1, "Name is required").max(200),
  reporter_email: z.string().trim().email("Enter a valid email").max(320),
  reporter_org: z.string().trim().max(200).optional(),
  severity: z.enum(["sev1", "sev2", "sev3", "vulnerability", "other"]),
  category: z.enum([
    "security_vulnerability",
    "data_exposure",
    "service_outage",
    "data_accuracy",
    "abuse",
    "other",
  ]),
  summary: z.string().trim().min(5, "Add a short summary").max(500),
  details: z.string().trim().min(10, "Tell us what happened").max(10000),
  affected_url: z.string().trim().max(500).optional().or(z.literal("")),
  contact_pref: z.enum(["email", "phone", "no_followup"]),
});

type FormState = z.infer<typeof schema>;

const initial: FormState = {
  reporter_name: "",
  reporter_email: "",
  reporter_org: "",
  severity: "sev3",
  category: "security_vulnerability",
  summary: "",
  details: "",
  affected_url: "",
  contact_pref: "email",
};

const IncidentReportForm = () => {
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const first = Object.values(parsed.error.flatten().fieldErrors)[0]?.[0];
      toast.error(first ?? "Please check the form and try again.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("incident_reports").insert({
      reporter_name: parsed.data.reporter_name,
      reporter_email: parsed.data.reporter_email.toLowerCase(),
      reporter_org: parsed.data.reporter_org || null,
      severity: parsed.data.severity,
      category: parsed.data.category,
      summary: parsed.data.summary,
      details: parsed.data.details,
      affected_url: parsed.data.affected_url || null,
      contact_pref: parsed.data.contact_pref,
      reporter_user_id: user?.id ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Couldn't submit your report. Please email security@emrkt.co directly.");
      return;
    }
    trackEvent("incident_report_submitted", {
      severity: parsed.data.severity,
      category: parsed.data.category,
    });
    setSubmitted(true);
    setForm(initial);
  };

  if (submitted) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center">
        <CheckCircle2 className="h-10 w-10 mx-auto text-primary mb-3" />
        <h3 className="font-display text-xl text-foreground mb-2">Report received</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Thank you. Our team has been notified and will acknowledge sev-1 and sev-2 reports within 4 hours,
          everything else within 1 business day. For active incidents, also email{" "}
          <a className="text-primary underline" href="mailto:security@emrkt.co">security@emrkt.co</a>.
        </p>
        <Button variant="outline" className="mt-5" onClick={() => setSubmitted(false)}>
          Submit another report
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 rounded-lg border border-border bg-card p-6">
      <div className="flex items-center gap-2 pb-3 border-b border-border">
        <ShieldAlert className="h-5 w-5 text-primary" />
        <h3 className="font-display text-xl text-foreground">Report an Incident or Vulnerability</h3>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ir-name">Your name *</Label>
          <Input
            id="ir-name"
            value={form.reporter_name}
            onChange={(e) => update("reporter_name", e.target.value)}
            maxLength={200}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ir-email">Email *</Label>
          <Input
            id="ir-email"
            type="email"
            value={form.reporter_email}
            onChange={(e) => update("reporter_email", e.target.value)}
            maxLength={320}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ir-org">Organization (optional)</Label>
        <Input
          id="ir-org"
          value={form.reporter_org}
          onChange={(e) => update("reporter_org", e.target.value)}
          maxLength={200}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Severity *</Label>
          <Select value={form.severity} onValueChange={(v) => update("severity", v as FormState["severity"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sev1">SEV-1 — site down / data at risk</SelectItem>
              <SelectItem value="sev2">SEV-2 — major feature broken</SelectItem>
              <SelectItem value="sev3">SEV-3 — minor issue or bug</SelectItem>
              <SelectItem value="vulnerability">Security vulnerability</SelectItem>
              <SelectItem value="other">Other / not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v) => update("category", v as FormState["category"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="security_vulnerability">Security vulnerability</SelectItem>
              <SelectItem value="data_exposure">Data exposure / privacy</SelectItem>
              <SelectItem value="service_outage">Service outage</SelectItem>
              <SelectItem value="data_accuracy">Data accuracy concern</SelectItem>
              <SelectItem value="abuse">Abuse / misuse</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ir-summary">Short summary *</Label>
        <Input
          id="ir-summary"
          value={form.summary}
          onChange={(e) => update("summary", e.target.value)}
          maxLength={500}
          placeholder="One sentence describing the issue"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ir-details">What happened? *</Label>
        <Textarea
          id="ir-details"
          value={form.details}
          onChange={(e) => update("details", e.target.value)}
          maxLength={10000}
          rows={6}
          placeholder="Steps to reproduce, expected vs. actual behavior, screenshots links, timestamps, any sensitive data involved."
          required
        />
        <p className="text-xs text-muted-foreground">
          Please don't paste secrets, full PII, or credentials. Reference them instead.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ir-url">Affected URL (optional)</Label>
          <Input
            id="ir-url"
            value={form.affected_url}
            onChange={(e) => update("affected_url", e.target.value)}
            maxLength={500}
            placeholder="https://emrkt.co/..."
          />
        </div>
        <div className="space-y-2">
          <Label>Contact preference *</Label>
          <Select
            value={form.contact_pref}
            onValueChange={(v) => update("contact_pref", v as FormState["contact_pref"])}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Reach me by email</SelectItem>
              <SelectItem value="phone">Phone (we'll request a number)</SelectItem>
              <SelectItem value="no_followup">No follow-up needed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground">
          We acknowledge SEV-1/SEV-2 within 4 hours; everything else within 1 business day.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-semibold"
        >
          {submitting ? "Submitting…" : "Submit report"}
        </Button>
      </div>
    </form>
  );
};

export default IncidentReportForm;